import axios from "axios";
import storageApi from "../api/storage";
import authApi from "../api/auth";
import i18n from "../utils/i18n";
import { toast } from "react-toastify";
import { addMinutes, isAfter } from "date-fns";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
});

let tokenRenewPromise: Promise<void> | null = null;
let loggingOut = false;

export const resetAuthState = () => {
  loggingOut = false;
  tokenRenewPromise = null;
};

const forceLogout = (inactivity?: boolean) => {
  if (loggingOut) {
    return;
  }
  loggingOut = true;
  authApi.logout(inactivity);
};

class TokenRenewError extends Error {
  constructor(public status?: number) {
    super(`Token renew failed${status ? ` with status ${status}` : ""}`);
    this.name = "TokenRenewError";
  }
}

const renewToken = async (token: string): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/Auth/TokenRenew`,
    {
      headers: {
        Authorization: token,
      },
    }
  );
  if (!response.ok) {
    throw new TokenRenewError(response.status);
  }

  const renewData = await response.json();
  if (
    !renewData?.token ||
    !renewData?.userMaxIdleMinutes ||
    !renewData?.tokenValidMinutes
  ) {
    throw new TokenRenewError();
  }

  storageApi.setToken(renewData.token);
  storageApi.setIdleMinutes(renewData.userMaxIdleMinutes);
  storageApi.setTokenRefreshTimeout(
    addMinutes(new Date(), renewData.userMaxIdleMinutes)
  );
  storageApi.setTokenTimeout(
    addMinutes(new Date(), renewData.tokenValidMinutes)
  );
};

api.interceptors.request.use(
  async (config) => {
    const tokenRefreshTimeout = storageApi.getTokenRefreshTimeout();
    const tokenTimeout = storageApi.getTokenTimeout();
    const token = storageApi.getToken();

    if (token && tokenRefreshTimeout && tokenTimeout) {
      // logout if token expired
      if (isAfter(new Date(), tokenTimeout)) {
        forceLogout(true);
        return Promise.reject(new Error("Session expired"));
      }

      // renew token if it's about to expire
      if (isAfter(new Date(), tokenRefreshTimeout)) {
        if (!tokenRenewPromise) {
          tokenRenewPromise = renewToken(token).finally(() => {
            tokenRenewPromise = null;
          });
        }

        try {
          await tokenRenewPromise;
        } catch (error) {
          if (error instanceof TokenRenewError && error.status === 401) {
            forceLogout(true);
            return Promise.reject(error);
          }
        }
      }
    }

    config.headers = {
      ...config.headers,
      Authorization: storageApi.getToken() ?? "",
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.request && !error.response) {
      // The request was made but no response was received
      toast.error(i18n.t("errors.noResponse"));
    } else if (!error.request && !error.response) {
      // Something happened in setting up the request that triggered an Error
      toast.error(i18n.t("errors.requestSetup"));
    } else if (
      error.response.status === 401 ||
      (error.response.status === 403 && !storageApi.getToken())
    ) {
      forceLogout();
    }
    return Promise.reject(error);
  }
);

export default api;
